import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchReactivationList } from "@services/scheduling";
import { setDoNotCall } from "@services/reactivation";
import { isAdmin as checkIsAdmin } from "@utils/access";
import PageTitleWithBack from "@components/common/PageTitleWithBack";
import ErrorBanner from "@components/shared/ErrorBanner";
import ReactivationListSection from "@components/scheduling/ReactivationListSection";
import Button from "@components/ui/Button";

/**
 * The call list for one clinic. An admin's clinic comes down from the page
 * (the picker lives in the tab row); a clinic user's comes from their token,
 * so they send none.
 */
export default function ReactivationList({
  onStartCampaign,
  onStartRandomCampaign,
  selectedClinicId,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // A row action's failure is not the list's failure: it must not take the
  // table down with it, so it gets its own dismissible banner.
  const [rowError, setRowError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const Admin = useMemo(() => checkIsAdmin(), []);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [includeRecent, setIncludeRecent] = useState(false);
  const [busyPatientId, setBusyPatientId] = useState(null);

  // A list that resolves after the admin moved to another clinic belongs to
  // the clinic they left; its stamp no longer matches and it is dropped.
  const reqRef = useRef(0);

  const clinicId = Admin ? selectedClinicId : undefined;

  const loadData = useCallback(async () => {
    if (Admin && !clinicId) return;

    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchReactivationList(clinicId, { includeRecent });
      if (reqRef.current !== reqId) return;
      setData(res);
    } catch (err) {
      if (reqRef.current !== reqId) return;
      setError(err.message || "Failed to load reactivation list");
    } finally {
      if (reqRef.current === reqId) setLoading(false);
    }
  }, [Admin, clinicId, includeRecent]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // external_patient_id is PMS-local and often a short integer, so "123" in
  // one clinic is a different person from "123" in the next. A selection that
  // survived the switch would silently pre-select the wrong patients.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [clinicId]);

  const SECTIONS = [
    { key: "recent_cancellations", title: "Cancellations & no-shows", selectable: true,
      emptyText: "No recent cancellations or no-shows" },
    { key: "dormant_patients", title: "Dormant 5–18 months", selectable: true,
      emptyText: "No dormant patients" },
    { key: "long_dormant", title: "Not seen in 18+ months", selectable: false,
      emptyText: "Nobody has been away 18 months or more" },
    { key: "inactive_pms", title: "Inactive in PMS", selectable: false,
      emptyText: "Nobody is marked inactive in the PMS" },
  ];

  const ruleOff = data?.summary?.long_dormant_rule_active === false;
  // An older backend sends only the first two lists; a section whose list is
  // absent is not shown at all, and the 18-month section hides when the rule
  // is off for the clinic (history too short).
  const visibleSections = SECTIONS.filter(
    (s) => Array.isArray(data?.[s.key]) && !(s.key === "long_dormant" && ruleOff)
  );

  const matches = (p) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.phone?.toLowerCase().includes(q);
  };
  const rowsOf = (section) => (data?.[section.key] || []).filter(matches);

  // Every row from the callable lists, for selection and the random pool.
  const allPatients = useMemo(() => {
    if (!data) return [];
    return [...(data.recent_cancellations || []), ...(data.dormant_patients || [])];
  }, [data]);
  const nothingShown = visibleSections.every((s) => rowsOf(s).length === 0);

  const toggleOne = (patient) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(patient.external_patient_id))
        next.delete(patient.external_patient_id);
      else next.add(patient.external_patient_id);
      return next;
    });
  };

  // A section's "Select all" hands up the rows it shows and can select; the
  // other section's selection is left alone.
  const toggleRows = (rows, select) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      rows.forEach((p) =>
        select
          ? next.add(p.external_patient_id)
          : next.delete(p.external_patient_id)
      );
      return next;
    });
  };

  // Selection survives filtering, so start from every row we know about.
  const selectedPatients = allPatients.filter((p) =>
    selectedIds.has(p.external_patient_id)
  );

  // Rows the backend could enroll from this list: a phone, not in a campaign,
  // and not inside the cooldown (rows the "Show recently contacted" toggle
  // reveals are still refused server-side). A cooldown of 0 means none.
  const cooldownDays = data?.summary?.cooldown_days || 0;
  const cooldownCutoff = cooldownDays > 0 ? Date.now() - cooldownDays * 86400000 : null;
  const eligibleCount = allPatients.filter(
    (p) => p.phone && !p.in_campaign &&
      !(cooldownCutoff && p.last_contacted_at && Date.parse(p.last_contacted_at) >= cooldownCutoff)
  ).length;

  const markDoNotCall = async (patient) => {
    setBusyPatientId(patient.external_patient_id);
    setRowError(null);
    try {
      await setDoNotCall(patient.external_patient_id, {
        doNotCall: !patient.do_not_call,
        clinicId: Admin ? clinicId : undefined,
      });
      await loadData();
    } catch (e) {
      // Deliberately not `setError`: that swaps the whole table for a retry
      // pane, so one failed PUT would hide every patient.
      setRowError(e.message || "Could not update the patient");
    } finally {
      setBusyPatientId(null);
    }
  };

  const loadingPane = (
    <div className="flex items-center justify-center py-20">
      <svg
        className="animate-spin h-8 w-8 text-blue-600"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span className="ml-3 text-gray-500">Loading reactivation list...</span>
    </div>
  );

  if (Admin && !clinicId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <svg
          className="w-12 h-12 mb-3 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9.27-3.11-11-7.5a11.72 11.72 0 013.168-4.477M9.88 9.88a3 3 0 104.24 4.24M15 12a3 3 0 00-3-3"
          />
        </svg>
        <p className="text-sm text-gray-500">
          Pick a clinic in the tab bar. Only clinics integrated with a scheduling
          provider are offered.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <PageTitleWithBack title="Patient Reactivation" />
          {data?.summary && (
            <p className="text-sm text-gray-500 -mt-4 ml-12">
              {data.summary.total_call_list} patient(s) to contact
              {" \u2014 "}
              {data.summary.total_cancelled_no_rebook} cancelled or no-show,{" "}
              {data.summary.total_dormant} dormant
              {!ruleOff &&
                typeof data.summary.total_long_dormant === "number" &&
                `; ${data.summary.total_long_dormant} not seen in 18+ months`}
              {typeof data.summary.total_inactive_pms === "number" &&
                `; ${data.summary.total_inactive_pms} inactive in PMS`}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m21 21-4.35-4.35M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search name, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Cooldown toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              aria-label="Show recently contacted"
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={includeRecent}
              onChange={(e) => setIncludeRecent(e.target.checked)}
            />
            Show recently contacted
          </label>
          {data?.summary?.hidden_cooldown > 0 && !includeRecent && (
            <span className="text-xs text-gray-500">
              {data.summary.hidden_cooldown} hidden, contacted in the last{" "}
              {data.summary.cooldown_days} days
            </span>
          )}

          {/* Refresh */}
          <button
            onClick={loadData}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Refresh
          </button>

          <Button
            color="light"
            size="sm"
            disabled={loading || !!error || eligibleCount === 0}
            onClick={() => onStartRandomCampaign(eligibleCount)}
          >
            Start with random patients
          </Button>
        </div>
      </div>

      {/* Selection bar */}
      {selectedPatients.length > 0 && (
        <div className="sticky top-0 z-10 mb-3 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <span className="text-sm text-blue-900">
            {selectedPatients.length} patient
            {selectedPatients.length === 1 ? "" : "s"} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              color="light"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={() => onStartCampaign(selectedPatients)}
            >
              Start campaign
            </Button>
          </div>
        </div>
      )}

      {rowError && (
        <ErrorBanner message={rowError} onDismiss={() => setRowError(null)} />
      )}

      {!loading && !error && ruleOff && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
          History covers {data.summary.history_coverage_months} months; the 18-month
          rule is off for this clinic.
        </p>
      )}

      {/* Loading and error both stay inside the layout so the controls stay
          put: a clinic the backend rejects must not blank the page, and a
          slow one must not flash the filters away on every switch. */}
      {loading ? (
        loadingPane
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Retry
          </button>
        </div>
      ) : nothingShown ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg
            className="w-12 h-12 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm">No patients need reactivation</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto min-h-0 flex flex-col gap-6">
          {visibleSections.map((section) => (
            <ReactivationListSection
              key={section.key}
              title={section.title}
              rows={rowsOf(section)}
              selectable={section.selectable}
              selectedIds={selectedIds}
              onToggleOne={toggleOne}
              onToggleRows={toggleRows}
              onMarkDoNotCall={markDoNotCall}
              busyPatientId={busyPatientId}
              emptyText={
                searchQuery.trim()
                  ? "No matches for this search"
                  : section.emptyText
              }
            />
          ))}
        </div>
      )}

    </div>
  );
}
